import { AbortError } from '@app/errors/AbortError'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { getPlacesReverse, getPlacesSuggest } from '@app/packages/api/v3/actions/places'
import type { V3PlaceSuggestion } from '@app/packages/api/v3/types/lib'
import type { Point } from '@app/packages/geo/Point'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { getAnalyticsRouter } from '@app/store/actions/analytics'
import { callApi } from '@app/store/actions/api'
import { chosenPlaceLocationResultSelector } from '@app/store/slices/profile_place'
import { createThunk } from '@app/store/thunk'

import type { AddressSuggestFilter, AddressSuggestion } from './addressSuggestion'
import { placeSuggestionGeocodeData, placeSuggestionValue, suggestNearHint } from './addressSuggestion'
import { sleep } from './sleep'

/** Every row is ours, so a validator or a label extractor is handed what the backend answered with. */
export type LabelExtractor = (place: V3PlaceSuggestion, kind: string, defaultValue: string) => string
export type GeocodeValidator = (place: V3PlaceSuggestion) => void

/** `filter` narrows what the backend suggests, cities alone for instance. */
export function getSuggestions(input: string, initiator: string, results = 5, filter: AddressSuggestFilter = {}) {
  return createThunk(async (dispatch): Promise<AddressSuggestion[]> => {
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // An address suggestion was asked of the geocoder. Measures the call, not a press.
        type: 'geosuggest_call',
        data: { provider: 'backend', initiator },
      })).set('amplitude')
    )
    return dispatch(getSuggestionsFromBackend(input, results, filter))
  })
}

/** The ranking hint is the visitor's chosen city, never where they are standing. */
export function getSuggestionsFromBackend(input: string, results: number, filter: AddressSuggestFilter) {
  return createThunk(async (dispatch): Promise<AddressSuggestion[]> => {
    const near = await dispatch(resolveThunkSelector(chosenPlaceLocationResultSelector))
    const response = await dispatch(callApi(getPlacesSuggest({ q: input, kind: filter.kind, near: near ? suggestNearHint(near) : undefined })))
    const rows = response.data.slice(0, results).map(place => ({ value: placeSuggestionValue(place), place }))
    // A pick is told apart by its value, so one value is one row.
    return rows.filter((row, index) => rows.findIndex(other => other.value === row.value) === index)
  })
}

/** A pick, as the geocoder would describe it. A row of ours already carries its point; a bare string is looked up by its text. */
export function getSuggestionGeocodeData(
  suggestion: AddressSuggestion,
  options: { extractLabel?: LabelExtractor; validate?: GeocodeValidator; filter?: AddressSuggestFilter } = {}
) {
  return createThunk(async (dispatch): Promise<GeocodeData | null> => {
    const place = suggestion.place ?? (await dispatch(getSuggestionsFromBackend(suggestion.value, 1, options.filter ?? {})))[0]?.place
    if (!place) return null
    return getGeocodeDataFromPlace(place, options)
  })
}

export type ReverseGeocodeOptions = {
  extractLabel?: LabelExtractor
  /** For a trigger that fires on every map move: the backend path waits for the map to settle, then drops a point it has moved past. */
  isStale?: () => boolean
}

/** The address at a point; `null` when nothing is near enough, outside Russia for one. */
export function getReverseGeocodeData(point: Point, { extractLabel, isStale }: ReverseGeocodeOptions = {}) {
  return createThunk(async (dispatch): Promise<GeocodeData | null> => {
    if (isStale) {
      // The backend's quota is shared, and a dragged map would otherwise ask at every pause.
      await sleep(MAP_SETTLE_MS)
      if (isStale()) throw new AbortError('The map moved on before it settled')
    }

    const response = await dispatch(callApi(getPlacesReverse({ latitude: point.lat, longitude: point.lon })))
    const [place] = response.data
    if (!place) return null
    return getGeocodeDataFromPlace(place, { extractLabel })
  })
}

export type GeocodeData = {
  label: string
  city: string
  kind: string
  location: Point
  bounds: {
    bottomLeft: Point
    topRight: Point
  }
}

const MAP_SETTLE_MS = 500

function getGeocodeDataFromPlace(place: V3PlaceSuggestion, options: { extractLabel?: LabelExtractor; validate?: GeocodeValidator }): GeocodeData {
  options.validate?.(place)
  const data = placeSuggestionGeocodeData(place)
  if (!options.extractLabel) return data
  return { ...data, label: options.extractLabel(place, data.kind, data.label) }
}
